import type { EntryKey } from "@pinia/colada";
import type { MaybeRefOrGetter } from "vue";
import type { AvailableDid, DidDestination } from "@/api/organization-did.api";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { toValue } from "vue";
import rest from "@/api/rest";
import { DASHBOARD_QUERY_KEYS } from "@/queries/dashboard.query";
import { ORGANIZATION_QUERY_KEYS } from "@/queries/organization.query";
import { USAGE_REQUESTS_QUERY_KEYS } from "@/queries/usage-requests.query";
import { isApiError } from "@/services/api";

/**
 * Query keys factory for organization DIDs following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const ORGANIZATION_DID_QUERY_KEYS = {
  root: ["organization-did"] as const,
  available: (projectId?: string) =>
    [...ORGANIZATION_DID_QUERY_KEYS.root, "available", projectId ?? "all"] as const,
  owned: () => [...ORGANIZATION_DID_QUERY_KEYS.root, "owned"] as const,
  providerPool: (ddd?: string) => [...ORGANIZATION_DID_QUERY_KEYS.root, "provider", ddd ?? "all"] as const,
  providerQuote: () => [...ORGANIZATION_DID_QUERY_KEYS.root, "provider", "quote"] as const,
};

// Keep uncertain purchase keys across panel remounts for the lifetime of the app.
const didPurchaseIdempotencyKeys = new Map<string, string>();

function idempotencyContextKey(number?: string, ddd?: string, agentId?: string): string {
  return `${number ?? ddd}:${agentId ?? "org"}`;
}

/**
 * Query hook for fetching available DIDs for the organization (org-wide list).
 *
 * Features:
 * - Organization-scoped (backend filters by organization_id)
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 * - Shared cache across components
 */
export function useAvailableDidsQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    key: ORGANIZATION_DID_QUERY_KEYS.available() as EntryKey,
    query: async () => {
      return await rest.organizationDid.getAvailableDids();
    },
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
    enabled: () => toValue(enabled),
  });
}

/**
 * Project-scoped variant of {@link useAvailableDidsQuery}. Returns the numbers
 * that the given project is allowed to link to an agent. The query stays
 * disabled until a project id is resolved so we never fire a request with an
 * empty `project_id`.
 */
export function useAvailableDidsForProject(
  projectId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  return useQuery({
    key: () => ORGANIZATION_DID_QUERY_KEYS.available(toValue(projectId)) as EntryKey,
    query: async () => rest.organizationDid.getAvailableDids(toValue(projectId)),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: () => toValue(enabled) && !!toValue(projectId),
  });
}

export function useOwnedDidsQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    key: ORGANIZATION_DID_QUERY_KEYS.owned() as EntryKey,
    query: async () => rest.organizationDid.getOwnedDids(),
    staleTime: 30_000,
    enabled: () => toValue(enabled),
  });
}

export function useProviderDidPoolQuery(
  ddd?: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  return useQuery({
    key: () => ORGANIZATION_DID_QUERY_KEYS.providerPool(ddd ? toValue(ddd) : undefined) as EntryKey,
    query: async () => rest.organizationDid.getProviderPool(ddd ? toValue(ddd) : undefined),
    staleTime: 15_000,
    enabled: () => toValue(enabled),
  });
}

export function useProviderDidQuoteQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    key: ORGANIZATION_DID_QUERY_KEYS.providerQuote() as EntryKey,
    query: async () => rest.organizationDid.getProviderQuote(),
    staleTime: 5 * 60_000,
    enabled: () => toValue(enabled),
  });
}

export function reconcileAllocatedDid(
  current: AvailableDid[] | undefined,
  did: AvailableDid,
): AvailableDid[] {
  const next = current ? [...current] : [];
  const existingIndex = next.findIndex(item => item.id === did.id);
  if (existingIndex === -1) next.push(did);
  else next[existingIndex] = did;
  return next;
}

export function reconcileReleasedDid(
  current: AvailableDid[] | undefined,
  didId: string,
): AvailableDid[] {
  return current?.filter(did => did.id !== didId) ?? [];
}

export function shouldRetainDidIdempotencyKey(error: unknown): boolean {
  return !isApiError(error) || !error.response || error.response.status === 504;
}

export function useAllocateProviderDid() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (
      { ddd, number, agentId, destination }: {
        ddd?: string;
        number?: string;
        agentId?: string;
        destination?: DidDestination;
      },
    ) => {
      const contextKey = idempotencyContextKey(number, ddd, agentId);
      const key = didPurchaseIdempotencyKeys.get(contextKey) ?? crypto.randomUUID();
      didPurchaseIdempotencyKeys.set(contextKey, key);
      try {
        return await rest.organizationDid.allocateProviderDid(
          { ddd, number, agentId, destination },
          key,
        );
      }
      catch (error) {
        if (!shouldRetainDidIdempotencyKey(error))
          didPurchaseIdempotencyKeys.delete(contextKey);
        throw error;
      }
    },
    onSuccess: (did, { ddd, number, agentId }) => {
      didPurchaseIdempotencyKeys.delete(idempotencyContextKey(number, ddd, agentId));
      queryCache.setQueryData(
        ORGANIZATION_DID_QUERY_KEYS.available() as EntryKey,
        (current?: AvailableDid[]) => reconcileAllocatedDid(current, did),
      );
      queryCache.setQueryData(
        ORGANIZATION_DID_QUERY_KEYS.owned() as EntryKey,
        (current?: AvailableDid[]) => reconcileAllocatedDid(current, did),
      );
    },
    onSettled: () => {
      queryCache.invalidateQueries({ key: ORGANIZATION_DID_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: DASHBOARD_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: USAGE_REQUESTS_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: ORGANIZATION_QUERY_KEYS.credits() as EntryKey });
    },
  });
}

/**
 * Update the destination (global vs. restricted-to-projects) of a DID.
 * Destination changes affect billing attribution, so we invalidate the
 * dashboard and org credits keys alongside the DID root — same rationale as
 * {@link useAllocateProviderDid}.
 */
export function useUpdateDidDestination() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async ({ didId, body }: { didId: string; body: DidDestination }) =>
      rest.organizationDid.updateDidDestination(didId, body),
    onSettled: () => {
      queryCache.invalidateQueries({ key: ORGANIZATION_DID_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: DASHBOARD_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: ORGANIZATION_QUERY_KEYS.credits() as EntryKey });
    },
  });
}

/**
 * Unlink a DID from whatever agent currently uses it. Agent linkage is surfaced
 * on the dashboard, so we invalidate it as well as the DID root and credits.
 */
export function useUnassignDid() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (didId: string) => rest.organizationDid.unassignDid(didId),
    onSettled: () => {
      queryCache.invalidateQueries({ key: ORGANIZATION_DID_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: DASHBOARD_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: ORGANIZATION_QUERY_KEYS.credits() as EntryKey });
    },
  });
}

/**
 * Link a DID to an agent. Same invalidation footprint as {@link useUnassignDid}:
 * the DID root (list + agent linkage), the dashboard, and org credits.
 */
export function useAssignDid() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async ({ didId, agentId }: { didId: string; agentId: string }) =>
      rest.organizationDid.assignDid(didId, agentId),
    onSettled: () => {
      queryCache.invalidateQueries({ key: ORGANIZATION_DID_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: DASHBOARD_QUERY_KEYS.root as EntryKey });
      queryCache.invalidateQueries({ key: ORGANIZATION_QUERY_KEYS.credits() as EntryKey });
    },
  });
}

export function useReleaseProviderDid() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (didId: string) => rest.organizationDid.releaseProviderDid(didId),
    onSuccess: (_releasedDid, didId) => {
      queryCache.setQueryData(
        ORGANIZATION_DID_QUERY_KEYS.available() as EntryKey,
        (current?: AvailableDid[]) => reconcileReleasedDid(current, didId),
      );
      queryCache.setQueryData(
        ORGANIZATION_DID_QUERY_KEYS.owned() as EntryKey,
        (current?: AvailableDid[]) => reconcileReleasedDid(current, didId),
      );
    },
    onSettled: () => {
      queryCache.invalidateQueries({ key: ORGANIZATION_DID_QUERY_KEYS.root as EntryKey });
    },
  });
}
